import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ShiftList({ rows, testid, empty }) {
  return (
    <div className="rounded-2xl border bg-white divide-y" data-testid={testid}>
      {rows.length === 0 && <p className="p-4 text-sm text-slate-400">{empty}</p>}
      {rows.map((s, i) => <div key={i} className="p-3 text-sm flex justify-between"><span><strong>{s.date}</strong> {s.start}–{s.end} {s.role ? `· ${s.role}` : ""}</span>
        <span className="text-slate-500">{s.org_name}{s.timesheet_status ? ` · ${s.timesheet_status}` : ""}</span></div>)}
    </div>
  );
}

export function ScheduleTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/my/schedule").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return <ShiftList rows={rows} testid="my-schedule" empty="No upcoming shifts." />;
}

export function HistoryTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/my/history").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return <ShiftList rows={rows} testid="my-history" empty="No work history yet." />;
}

export function AvailabilityTab() {
  const [d, setD] = useState({ available_for_freelance: true, rules: [] });
  const [f, setF] = useState({ status: "available", kind: "weekday", weekday: "0", date: "", all_day: true, start: "09:00", end: "17:00", note: "" });
  const load = () => api.get("/my/availability").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const add = () => api.post("/my/availability", { status: f.status, all_day: f.all_day, start: f.start, end: f.end, note: f.note,
    ...(f.kind === "weekday" ? { weekday: Number(f.weekday) } : { date: f.date }) }).then(() => { toast.success("Saved"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  const master = (v) => api.put("/my/availability/settings", { available_for_freelance: v }).then(load);
  return (
    <div className="space-y-3" data-testid="my-availability">
      <div className="rounded-2xl border bg-white p-4 flex justify-between items-center"><span className="font-medium">Available for freelance work</span>
        <Switch checked={d.available_for_freelance} onCheckedChange={master} data-testid="my-availability-master" /></div>
      <div className="rounded-2xl border bg-white p-4 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="my-av-status"><SelectValue /></SelectTrigger>
          <SelectContent>{["available", "unavailable", "tentative", "ask_me"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger data-testid="my-av-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="weekday">Every week</SelectItem><SelectItem value="date">One date</SelectItem></SelectContent></Select>
        {f.kind === "weekday" ? <Select value={f.weekday} onValueChange={(v) => setF({ ...f, weekday: v })}><SelectTrigger data-testid="my-av-weekday"><SelectValue /></SelectTrigger>
          <SelectContent>{WD.map((w, i) => <SelectItem key={w} value={String(i)}>{w}</SelectItem>)}</SelectContent></Select>
          : <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="my-av-date" />}
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={f.all_day} onChange={(e) => setF({ ...f, all_day: e.target.checked })} data-testid="my-av-allday" /> All day</label>
        {!f.all_day && <><Input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="my-av-start" /><Input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="my-av-end" /></>}
        <Input placeholder="Private note (never shared)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="my-av-note" />
        <Button onClick={add} data-testid="my-av-add">Add</Button>
      </div>
      <div className="rounded-2xl border bg-white divide-y">
        {d.rules.map((r) => <div key={r.availability_id} className="p-3 text-sm flex justify-between" data-testid={`my-av-rule-${r.availability_id}`}>
          <span>{r.status.replace("_", " ")} · {r.date || `every ${WD[r.weekday]}`} · {r.all_day ? "all day" : `${r.start}–${r.end}`}</span>
          <button className="text-red-500 text-xs" onClick={() => api.delete(`/my/availability/${r.availability_id}`).then(load)} data-testid={`my-av-del-${r.availability_id}`}>Remove</button></div>)}
      </div>
      <p className="text-xs text-slate-400">Businesses only ever see Available, Unavailable, Tentative or Ask me — never where else you work or why.</p>
    </div>
  );
}
